<template>
  <div v-if="activeTab === 'overview'" class="row">

    <!-- Progress Chart and Steps Section -->
    <div class="col-12 mt-4">
      <div class="row g-4">
        <!-- Left Card: Progress Chart -->
        <div class="col-md-6">
          <div class="card shadow border rounded-3 p-3 h-100">
            <div class="card-body d-flex flex-column">
              <div class="d-flex align-items-center">
                <label class="fs-6 text-muted fw-bold mb-0 me-auto">Workflow Progress</label>
              </div>
              <hr>
              <div class="flex-grow-1 d-flex align-items-center justify-content-center">
                <div id="workflow-progress-chart"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- Right Card: Timeline Steps -->
        <div class="col-md-6">
          <div class="card shadow border rounded-3 p-3 h-100">
            <div class="card-body d-flex flex-column">
              <div class="d-flex align-items-center">
                <label class="fs-6 text-muted fw-bold mb-0 me-auto">Workflow Steps</label>
              </div>
              <hr>
              <div class="timeline-container flex-grow-1 d-flex flex-column">
                <div class="current-step-badge mb-3">
                  <div class="step-badge p-3 rounded-3 border">
                    <div class="d-flex align-items-center">
                      <div class="step-number rounded-circle me-3 d-flex align-items-center justify-content-center">
                        {{ current_step + 1 }}
                      </div>
                      <div>
                        <div class="fw-bold">{{ workflow_steps[current_step] }}</div>
                        <div class="text-xs text-muted">Step {{ current_step + 1 }} of {{ workflow_steps.length }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="workflow-progress-container">
                  <div class="step-descriptions">
                    <div
                      v-for="(step, index) in workflow_steps_status"
                      :key="index"
                      class="step-description-item d-flex mb-2 p-2 rounded-3"
                      :class="{
                        'completed': index < current_step,
                        'active': index === current_step,
                        'pending': index > current_step
                      }"
                    >
                      <div class="step-status-icon me-3 d-flex align-items-center justify-content-center">
                        <i v-if="index < current_step" class="fas fa-check"></i>
                        <i v-else-if="index === current_step" class="fas fa-arrow-right"></i>
                        <span v-else>{{ index + 1 }}</span>
                      </div>
                      <div class="step-text">
                        <span class="text-sm">{{ step.step_name }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from "@/config";

export default {
  name: 'OverviewTab',
  props: {
    activeTab: {
      type: String,
      default: 'overview'
    },
    workflowData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      workflow_steps: [
        "Create Workflow",
        "Choose Services",
        "Arrange Services",
        "Terms and Conditions",
        "Reasons for Usage",
        "Workflow Router",
        "Confirm Workflow",
      ],
      workflow_data: {},
      chart_instance: null, // Store chart instance reference
    };
  },
  watch: {
    current_step(new_value) {
      if (new_value >= 0 && this.ApexCharts) {
        this.create_chart();
      }
    },
    activeTab(new_value) {
      // Recreate chart when switching back to overview tab
      if (new_value === 'overview' && this.ApexCharts && this.current_step >= 0) {
        // Small delay to ensure DOM is ready after tab switch
        this.$nextTick(() => {
          this.create_chart();
        });
      }
    }
  },
  computed: {
    workflow_steps_status() {
      if (!this.workflow_data) {
        return [];
      }

      return [
        {
          step_name: "Create Workflow",
          status: this.workflow_data.workflow_exists ? "completed" : "pending",
        },
        {
          step_name: "Choose Services",
          status: this.workflow_data.workflow_services_exist ? "completed" : "pending",
        },
        {
          step_name: "Arrange Services",
          status: this.workflow_data.workflow_services_order_exists ? "completed" : "pending",
        },
        {
          step_name: "Terms and Conditions",
          status: this.workflow_data.selected_use_terms_and_conditions ? "completed" : "pending",
        },
        {
          step_name: "Reasons for Usage",
          status: this.workflow_data.reason_exists ? "completed" : "pending",
        },
        {
          step_name: "Workflow Router",
          status: this.workflow_data.reason_exists ? "completed" : "pending",
        },
        {
          step_name: "Confirm Workflow",
          status: this.workflow_data.confirmed_workflow_exists ? "completed" : "pending",
        },
      ];
    },
    current_step() {
      // Returns the index of the first step that is not completed, or last if all are completed
      const steps = this.workflow_steps_status;
      const idx = steps.findIndex(step => step.status !== "completed");
      return idx === -1 ? steps.length - 1 : idx;
    },
    workflow_id() {
      return this.workflowData?.id || this.$store?.getters?.get_workflow_id;
    },
    ApexCharts() {
      return window.ApexCharts || null;
    },
  },
  methods: {
    capitalizeString(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    create_chart() {
      if (this.ApexCharts) {
        // Destroy previous chart instance if it exists
        if (this.chart_instance) {
          this.chart_instance.destroy();
          this.chart_instance = null;
        }

        const percent = Math.round(
          ((this.current_step + 1) / this.workflow_steps.length) * 100
        );
        const options = {
          series: [percent],
          chart: {
            height: 350,
            type: "radialBar",
            toolbar: { show: false },
          },
          plotOptions: {
            radialBar: {
              startAngle: -135,
              endAngle: 225,
              hollow: {
                margin: 0,
                size: "70%",
                background: "#fff",
                position: "front",
                dropShadow: {
                  enabled: true,
                  top: 3,
                  left: 0,
                  blur: 4,
                  opacity: 0.5,
                },
              },
              track: {
                background: "#fff",
                strokeWidth: "67%",
                margin: 0,
                dropShadow: {
                  enabled: true,
                  top: -3,
                  left: 0,
                  blur: 4,
                  opacity: 0.7,
                },
              },
              dataLabels: {
                show: true,
                name: {
                  offsetY: -10,
                  show: true,
                  color: "#888",
                  fontSize: "15px",
                },
                value: {
                  formatter: function (val) {
                    return parseInt(val) + "%";
                  },
                  color: "#111",
                  fontSize: "28px",
                  show: true,
                },
              },
            },
          },
          fill: {
            type: "gradient",
            gradient: {
              shade: "dark",
              type: "horizontal",
              shadeIntensity: 0.5,
              gradientToColors: [
                getComputedStyle(document.documentElement)
                  .getPropertyValue("--primary-color")
                  .trim() || "#ABE5A1",
              ],
              inverseColors: true,
              opacityFrom: 1,
              opacityTo: 1,
              stops: [0, 100],
            },
          },
          stroke: {
            lineCap: "round",
          },
          labels: ["Workflow Progress"],
        };
        const chart = new this.ApexCharts(
          document.querySelector("#workflow-progress-chart"),
          options
        );
        this.chart_instance = chart;
        chart.render();
      }
    },
    get_workflow_information() {
      const TOKEN = localStorage.getItem("token");
      const GET_WORKFLOW_INFORMATION = `${API_BASE_URL}/verify_api/workflow_management_api/get_workflow_data_api/${this.workflow_id}`;

      $.ajax({
        url: GET_WORKFLOW_INFORMATION,
        method: "GET",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${TOKEN}`
        },
        beforeSend: () => {
          // Loading handled by parent component
        },
        success: (response) => {
          if (response.workflow_data) {
            // We don't need the full workflow data here, just call edit_workflow_progress
            this.edit_workflow_progress();
          }
        },
        error: (error) => {
          console.warn("Error fetching workflow information:", error);
        },
      });
    },
    edit_workflow_progress() {
      const TOKEN = localStorage.getItem("token");
      const GET_WORKFLOW_PROGRESS = `${API_BASE_URL}/verify_api/workflow_management_api/edit_workflow_progress_api/${this.workflow_id}`;

      $.ajax({
        url: GET_WORKFLOW_PROGRESS,
        method: "GET",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${TOKEN}`
        },
        beforeSend: () => {
          // Loading handled by parent component
        },
        success: (response) => {
          this.workflow_data = response; // Update workflow_data for the steps
        },
        error: (error) => {
          console.warn("Error fetching workflow progress:", error);
        },
      });
    },
  },
  mounted() {
    if (this.workflow_id) {
      this.get_workflow_information();
    }
    if (this.current_step >= 0 && this.ApexCharts) {
      this.create_chart();
    }
  },
  beforeUnmount() {
    // Destroy chart instance if it exists
    if (this.chart_instance) {
      this.chart_instance.destroy();
      this.chart_instance = null;
    }

    const chart_ids = [
      "#workflow-progress-chart"
    ];
    chart_ids.forEach((id) => {
      const container = document.querySelector(id);
      if (container) container.innerHTML = "";
    });
  },
}
</script>

<style scoped>
.overview-card {
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.overview-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.icon-wrapper {
  background: rgba(0, 123, 255, 0.1);
  border-radius: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Timeline Styles */
.timeline-container {
  position: relative;
  padding: 0.5rem;
}

/* Current Step Badge Styling */
.current-step-badge .step-badge {
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.current-step-badge .step-number {
  background-color: var(--primary-color, #007bff);
  color: white;
  width: 40px;
  height: 40px;
  font-weight: bold;
  font-size: 16px;
}

/* Step Descriptions */
.step-description-item {
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
  border: #dcdcdc 1px solid;
}

.step-description-item:hover {
  border-color: #c3c7ca;
  box-shadow: 0 2px 8px rgba(33, 37, 41, 0.08), 0 0 0 2px #dee2e6;
  cursor: pointer;
}

.step-description-item.completed {
  background-color: rgba(40, 167, 69, 0.05);
}

.step-description-item.active {
  background-color: rgba(var(--primary-color-rgb, 0, 123, 255), 0.05);
}

.step-description-item.pending {
  color: #6c757d;
}

.step-status-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 12px;
}

.step-description-item.completed .step-status-icon {
  background-color: #28a745;
  color: white;
}

.step-description-item.active .step-status-icon {
  background-color: var(--primary-color, #007bff);
  color: white;
}

.step-description-item.pending .step-status-icon {
  background-color: #e9ecef;
  color: #6c757d;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .current-step-badge .step-number {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }

  .step-status-icon {
    width: 24px;
    height: 24px;
  }
}
</style>
